'use client';

import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User } from '@/app/types/user';
import type { StripeSubscription } from '@/app/actions/stripe';
import type { FeatureFlags } from '@puzzlio/core/featureFlags';
import { cacheFeatureFlags } from '@/app/lib/feature-flags-cache';

export interface MeResponse {
  user: User;
  subscription: StripeSubscription | null;
  /** The name the account comments under. Null until one is chosen, and for anyone signed out. */
  screenName: string | null;
  /** Whether the account's email is on the daily reminder list. False for anyone signed out. */
  dailyReminders: boolean;
  /** Whether each feature flag is on for this player; read them with useFeatureFlag. */
  features: FeatureFlags;
}

/** Shared query key + staleTime for the `/api/me` payload (user + subscription). */
export const USER_QUERY_KEY = ['user'] as const;
export const USER_QUERY_STALE_TIME = 5 * 60 * 1000;

// JSON-serialized subscription dates arrive as ISO strings; revive them so StripeSubscription's Date
// fields hold real Dates for downstream consumers.
function reviveSubscription(s: StripeSubscription | null): StripeSubscription | null {
  if (!s) return null;
  return {
    ...s,
    renewsAt: new Date(s.renewsAt),
    cancelAt: s.cancelAt ? new Date(s.cancelAt) : null,
    graceEndsAt: s.graceEndsAt ? new Date(s.graceEndsAt) : null,
  };
}

export async function fetchMe(): Promise<MeResponse> {
  // The device's time zone keeps a signed-in player's profile current (see app/api/me).
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const res = await fetch('/api/me', { headers: zone ? { 'X-Timezone': zone } : {} });
  if (!res.ok) throw new Error('Failed to fetch user');
  const data = (await res.json()) as MeResponse;
  const features = data.features ?? {};
  cacheFeatureFlags(features);
  return {
    user: data.user,
    subscription: reviveSubscription(data.subscription),
    screenName: data.screenName ?? null,
    dailyReminders: data.dailyReminders ?? false,
    features,
  };
}

// Shared options for the `/api/me` query. BOTH observers (UserProvider and
// useStripeSubscription) must use these: TanStack refetches on window focus if *any*
// observer opts in, so refetchOnWindowFocus lives here to keep them in lockstep.
// We keep it off — the same-tab subscribe → Stripe Checkout → return flow refreshes via
// the full-page reload on return (initial fetch), so focus refetching isn't needed and
// only adds stray /api/me calls (each of which hits Supabase auth + the Stripe API).
export const userQueryOptions = {
  queryKey: USER_QUERY_KEY,
  queryFn: fetchMe,
  staleTime: USER_QUERY_STALE_TIME,
  refetchOnWindowFocus: false,
};

interface UserContextValue {
  user: User | null;
  isLoading: boolean;
}

const UserContext = createContext<UserContextValue>({ user: null, isLoading: true });

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending } = useQuery(userQueryOptions);

  return (
    <UserContext.Provider value={{ user: data?.user ?? null, isLoading: isPending }}>{children}</UserContext.Provider>
  );
}

export function useUser(): User | null {
  return useContext(UserContext).user;
}

/** Returns [user, isLoading] — use this when you need both, to avoid two separate hook calls. */
export function useUserLoading(): [User | null, boolean] {
  const { user, isLoading } = useContext(UserContext);
  return [user, isLoading];
}
